/* eslint-disable @typescript-eslint/no-use-before-define */

import { useUserIdentity } from '@src/stores-v2';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import InfoIconAlt from '@src/svgs/InfoIconAlt';
import ClipsCameraIcon from '@src/svgs/quick-actions/VideoIcon';
import SparkleIcon from '@src/svgs/SparkleIcon';
import { getThemeTooltip } from '@src/utility/themeTooltips';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import AIClipProducerResults from '../ClipProducer/AIClipProducerResults';
import ClipDurationButtonList from '../ClipProducer/ClipDurationButtonList';
import AudienceFilterComponent from '../common/AudienceFilterComponent';
import { PrimaryButton } from '../common/dialogStyles';
import GoalFilterComponent from '../common/GoalFilterComponent';
import { ProduceIcon } from '../common/icons';
import Modal from '../common/Modal';
import SpeakerFilterComponent from '../common/SpeakerFilterComponent';
import Switch from '../common/Switch';
import Textarea from '../common/Textarea';
import { PARAGRAPH_TOOLTIP_STYLE } from '../common/tooltipStyles';
import { VideoTemplateFilterComponent } from '../common/VideoTemplateFilterComponent';
import SpinningLoading from '../SpinningLoading';

import type { ClipProducerUIProps } from '../ClipProducer/AIClipProducer';

function ClipProducerSidebarUI(props: ClipProducerUIProps) {
  const {
    supportedPresets,
    isProducing,
    retryCount,
    onProductionCancel,
    selectedDuration,
    setSelectedDuration,
    selectedPresets,
    setSelectedPresets,
    onPresetClick,
    customPresetText,
    setCustomPresetText,
    producedClips,
    showModal,
    isProducingDisable,
    onProduceButtonClick,
    closeProducingModal,
    // Suggested topics props
    suggestedThemes,
    selectedSuggestedTheme,
    onSuggestedThemeClick,
    // New speaker selection props
    speakers,
    selectedSpeakers,
    setSelectedSpeakers,
    useSpeakerFilter,
    setUseSpeakerFilter,
    isLoadingSpeakers,
    showSpeakerSelector,
    // Goal selection props
    selectedGoal,
    setSelectedGoal,
    useGoalFilter,
    setUseGoalFilter,
    // Audience selection props
    selectedAudiences,
    setSelectedAudiences,
    useAudienceFilter,
    setUseAudienceFilter,
    // Video Template selection
    selectedVideoTemplateId,
    setSelectedVideoTemplateId,
    useVideoTemplateFilter,
    setUseVideoTemplateFilter,
    // Feature flag for custom preset input
    enableCustomPreset,
  } = props;

  const [useCustomPrompt, setUseCustomPrompt] = useState(false);
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const { enableClipProducerVideoTemplateSelection } = useFlagsCombination(
    userIdentity.currentRole,
  );
  const goals = videoCreator.organization?.branding?.goals || [];
  const audiences = videoCreator.organization?.branding?.audiences || [];

  // Validation state
  const [themeError, setThemeError] = useState<string | null>(null);
  const themeSectionRef = useRef<HTMLDivElement>(null);

  const handleProduceClick = () => {
    if (isProducing) return;
    if (
      !(selectedPresets.length || customPresetText || selectedSuggestedTheme)
    ) {
      setThemeError(
        useCustomPrompt
          ? 'Please describe your theme to continue'
          : 'Please select a theme to continue',
      );
      themeSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }
    setThemeError(null);
    onProduceButtonClick();
  };

  return (
    <Wrapper>
      {isProducing && (
        <ProducingOverlay>
          <SpinningLoading
            Ico={<ProduceIcon width="24" height="24" />}
            text={`Generating clips${
              retryCount > 0 ? `. Retrying (${retryCount})` : ''
            }`}
            onHide={retryCount > 0 ? onProductionCancel : undefined}
            hideText="Cancel"
            contentStyle={{ gap: '16px' }}
          />
        </ProducingOverlay>
      )}

      <PresetsList ref={themeSectionRef} hasError={!!themeError}>
        <Heading>
          Find social clips based on theme <RequiredMark>*</RequiredMark>
        </Heading>
        {!useCustomPrompt &&
          supportedPresets.map(preset => {
            const isActive = selectedPresets.includes(preset.title);
            return (
              <PresetOption
                active={isActive}
                key={preset.title}
                onClick={() => {
                  setUseCustomPrompt(false);
                  setCustomPresetText('');
                  setThemeError(null);
                  onPresetClick(preset.title);
                }}
              >
                <PresetRadio active={isActive}>
                  {isActive && <PresetRadioDot />}
                </PresetRadio>
                <PresetLabel>
                  {preset.title}
                  <ThemeInfoIcon
                    data-tooltip-id="theme-tooltip"
                    data-tooltip-content={getThemeTooltip(
                      preset.title,
                      preset.userPromptDescription,
                    )}
                    onClick={e => e.stopPropagation()}
                  >
                    <InfoIconAlt width="10" height="10" />
                  </ThemeInfoIcon>
                </PresetLabel>
              </PresetOption>
            );
          })}
        {enableCustomPreset && (
          <CustomPromptSection
            onClick={() => {
              const next = !useCustomPrompt;
              setUseCustomPrompt(next);
              if (next) {
                setSelectedPresets([]);
              } else {
                setCustomPresetText('');
              }
            }}
          >
            <CustomPromptHeader>
              <CustomPromptLabel>
                <CustomPromptIcon>
                  {/* strokeColor is mandatory: SparkleIcon defaults to a
                      hardcoded #17C964. currentColor inherits the wrapper. */}
                  <SparkleIcon
                    width="12"
                    height="12"
                    strokeColor="currentColor"
                  />
                </CustomPromptIcon>
                <CustomPromptText>
                  <CustomPromptTitle>Describe your own theme</CustomPromptTitle>
                </CustomPromptText>
              </CustomPromptLabel>
              <Switch
                label=""
                checked={useCustomPrompt}
                onClick={e => {
                  e.stopPropagation();
                  const next = !useCustomPrompt;
                  setUseCustomPrompt(next);
                  if (next) {
                    setSelectedPresets([]);
                  } else {
                    setCustomPresetText('');
                  }
                }}
                small
              />
            </CustomPromptHeader>
            {useCustomPrompt && (
              // The guard, not the field, owns stopPropagation: Textarea
              // forwards onClick to the inner <textarea> only, so a click on the
              // wrapper would reach CustomPromptSection's handler — which calls
              // setCustomPresetText(''), silently wiping what the user typed.
              <TextareaGuard onClick={e => e.stopPropagation()}>
                <CustomPresetTextArea
                  id="customPresetInput"
                  fullWidth
                  placeholder="Type in what you'd like to see (e.g., 'Moments where speaker discusses leadership challenges')"
                  value={customPresetText}
                  onChange={e => {
                    setCustomPresetText(e.target.value);
                    if (e.target.value) setThemeError(null);
                  }}
                  autoFocus
                />
              </TextareaGuard>
            )}
          </CustomPromptSection>
        )}
        {themeError && (
          <ValidationErrorMessage>{themeError}</ValidationErrorMessage>
        )}
      </PresetsList>

      {/* Suggested topics section */}
      {suggestedThemes?.length > 0 && (
        <PresetsList>
          <Heading>Suggested topics</Heading>
          {suggestedThemes.map(title => {
            const isActive = selectedSuggestedTheme === title;
            return (
              <ThemePresetOption
                key={title}
                active={isActive}
                onClick={() => {
                  setThemeError(null);
                  onSuggestedThemeClick(title);
                }}
              >
                <PresetLabel>{title}</PresetLabel>
                <PresetRadio active={isActive}>
                  {isActive && <PresetRadioDot />}
                </PresetRadio>
              </ThemePresetOption>
            );
          })}
        </PresetsList>
      )}

      <FilterSection>
        <Heading>Choose your desired length in seconds</Heading>
        <ClipDurationButtonList
          size="sm"
          setSelectedDuration={setSelectedDuration}
          selectedDuration={selectedDuration}
        />
      </FilterSection>

      <CompactSections>
        {/* Goal selection */}
        {goals.length > 0 && (
          <GoalFilterComponent
            goals={goals}
            selectedGoal={selectedGoal}
            setSelectedGoal={setSelectedGoal}
            useGoalFilter={useGoalFilter}
            setUseGoalFilter={setUseGoalFilter}
            allowNone
            align="left"
          />
        )}

        {/* Audience selection */}
        {audiences.length > 0 && (
          <AudienceFilterComponent
            audiences={audiences}
            selectedAudiences={selectedAudiences}
            setSelectedAudiences={setSelectedAudiences}
            useAudienceFilter={useAudienceFilter}
            setUseAudienceFilter={setUseAudienceFilter}
            align="left"
          />
        )}

        {/* Speaker selection UI - only show if enabled */}
        {showSpeakerSelector && (
          <SpeakerFilterComponent
            speakers={speakers}
            selectedSpeakers={selectedSpeakers}
            setSelectedSpeakers={setSelectedSpeakers}
            useSpeakerFilter={useSpeakerFilter}
            setUseSpeakerFilter={val =>
              setUseSpeakerFilter(
                typeof val === 'function' ? val(useSpeakerFilter) : val,
              )
            }
            isLoadingSpeakers={isLoadingSpeakers}
            align="left"
          />
        )}
        {enableClipProducerVideoTemplateSelection && (
          <VideoTemplateFilterComponent
            useVideoTemplateFilter={useVideoTemplateFilter}
            setUseVideoTemplateFilter={setUseVideoTemplateFilter}
            selectedVideoTemplateId={selectedVideoTemplateId}
            setSelectedVideoTemplateId={setSelectedVideoTemplateId}
            align="left"
          />
        )}
      </CompactSections>

      <ProduceButton onClick={handleProduceClick} disabled={isProducingDisable}>
        <ClipsCameraIcon width="16" height="16" /> Produce
      </ProduceButton>

      {showModal && Boolean(producedClips?.length) && (
        <Modal
          isOpen
          closeModal={closeProducingModal}
          contentStyle={{
            marginTop: '70px',
            marginBottom: '70px',
          }}
        >
          <AIClipProducerResults
            onClose={closeProducingModal}
            fragments={producedClips!}
            isProducing={isProducing}
            generationContext={{
              themes: selectedPresets,
              customPrompt: customPresetText || undefined,
              suggestedTheme: selectedSuggestedTheme,
              duration: selectedDuration,
              speakers: useSpeakerFilter
                ? selectedSpeakers.map(s => s.name || s.id)
                : undefined,
              goal: useGoalFilter ? selectedGoal?.label : undefined,
              audiences: useAudienceFilter
                ? selectedAudiences.map(a => a.label)
                : undefined,
            }}
          />
        </Modal>
      )}
      <Tooltip
        id="theme-tooltip"
        noArrow
        opacity={1}
        style={PARAGRAPH_TOOLTIP_STYLE}
        offset={8}
      />
    </Wrapper>
  );
}

export default ClipProducerSidebarUI;

const Wrapper = styled.div`
  /* Top only — the card's Body owns the bottom spacing (see Speakers). */
  padding: 8px 0 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/** Tints the producing spinner; was an inline style on a bare div. */
const ProducingOverlay = styled.div`
  color: var(--primary);
`;

/**
 * Group labels, deliberately quieter than the controls under them.
 *
 * These were --foreground at semi-bold, then --foreground at medium — either way
 * the loudest thing in the panel, and still a weight step above the shared filter
 * rows right beneath them ("Target Goal", "Target Audiences"), which are
 * --foreground / --text-xs at normal weight. Three near-white bold lines stacked
 * down a 350px rail is what made this tab read heavier than the others.
 *
 * --text-secondary is the doc's spec for a row/group label and puts these one
 * step back, so the theme cards and duration buttons lead and the labels
 * annotate. Note the rail is inconsistent about this: Speakers uses --text-muted,
 * while Framing, Captions and Video Templates use --foreground. Worth unifying.
 */
const Heading = styled.div`
  width: 100%;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
`;

/**
 * The panel's single primary action, on the shared primary standard.
 *
 * Was a `styled.div`, which meant `disabled` was purely cosmetic — it greyed the
 * button out but `onClick` still fired, so Produce could start a run with no
 * original video (`isProducingDisable` covers that case but `handleProduceClick`
 * only guards `isProducing`). A real `<button>` makes the attribute do what it
 * already looked like it was doing, and brings keyboard access with it.
 *
 * `PrimaryButton` supplies --primary, --font-weight-semi-bold (down from bold),
 * the `:hover` state it never had, `:disabled { opacity: .6 }` and
 * --btn-h-lg (40px) — the app-wide primary height. Only full width is added; the
 * button previously stood at 48px, taller than every other primary in the app.
 */
const ProduceButton = styled(PrimaryButton)`
  width: 100%;
`;

const PresetsList = styled.div<{ hasError?: boolean }>`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  ${({ hasError }) =>
    hasError &&
    css`
      border: 1px solid var(--destructive);
      border-radius: var(--radius);
      padding: 12px;
    `}
`;

const RequiredMark = styled.span`
  color: var(--destructive);
  margin-left: 2px;
`;

const ValidationErrorMessage = styled.div`
  color: var(--destructive);
  padding: 8px 12px;
  background-color: color-mix(in srgb, var(--destructive) 10%, transparent);
  border-radius: var(--radius);
  font-size: var(--text-xs);
  width: 100%;
  box-sizing: border-box;
`;

const FilterSection = styled.div`
  width: 100%;
`;

// 16px rather than the doc's 24px section spacing. These four filters are
// collapsed single-line checkbox rows almost all of the time, and 24px between
// three of them reads as unrelated items rather than one group. Each filter keeps
// its own internal 8px gap between its row and its expanded options, so an open
// section is still clearly separated at a 2:1 ratio. The same block is
// re-declared in ClipProducerModalUI, AIProducerModal and
// ReelProducerConfigModal — extracting it touches out-of-scope files.
const CompactSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  width: 100%;
`;

const PresetOption = styled.div.attrs((props: { active: boolean }) => props)`
  padding: var(--spacing-sm) 12px;
  border-radius: var(--radius);
  width: calc(50% - 4px);
  box-sizing: border-box;
  border: 1px solid var(--border);
  background-color: var(--surface);
  color: var(--foreground);
  cursor: pointer;
  transition:
    background-color var(--transition-fast) ease,
    border-color var(--transition-fast) ease;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);

  &:hover ${() => ThemeInfoIcon} {
    opacity: 1;
  }

  &:hover {
    background-color: var(--surface-elevated);
    border-color: var(--border);
  }
`;

const PresetLabel = styled.span`
  color: inherit;
  font-size: var(--text-xs);
  /* Normal, per the doc's "row titles are normal weight, not medium". */
  font-weight: var(--font-weight-normal);
  line-height: 1.3;
  flex: 1;
  /* Always two lines tall. The longest titles ("Thought Leadership", "Product
     Features") do not fit on one line in a 155px cell, and squeezing padding to
     force them onto one only bought 2px of headroom — fragile, and it left the
     taller cards beside them looking half-empty. Reserving two lines makes every
     card in the grid the same height; centring keeps a one-line label looking
     deliberate rather than clipped to the top. */
  display: flex;
  align-items: center;
  min-height: 2.6em;
`;

const ThemeInfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 4px;
  vertical-align: middle;
  opacity: 0;
  transition:
    opacity var(--transition-fast) ease,
    color var(--transition-fast) ease;

  &:hover {
    color: inherit;
  }
`;

const PresetRadio = styled.div<{ active: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 14px;
  border: 1px solid
    ${props => (props.active ? 'var(--accent)' : 'var(--text-muted)')};
  background: ${props => (props.active ? 'var(--background)' : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PresetRadioDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
`;

const ThemePresetOption = styled(PresetOption)`
  word-break: break-word;
`;

const CustomPromptSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  /* Matches PresetOption. This was the only bordered surface in the panel with
     no fill, so it read as a floating outline beside its filled siblings. */
  background-color: var(--surface);
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color var(--transition-fast) ease;
`;

const CustomPromptHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const CustomPromptLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Stays --primary: this row is the AI-authored path, and the green matches the
// producing spinner and the identical row in Content Studio, so the two surfaces
// read as one family.
const CustomPromptIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--primary);
  flex-shrink: 0;
`;

const CustomPromptText = styled.div`
  display: flex;
  flex-direction: column;
`;

const CustomPromptTitle = styled.span`
  color: var(--foreground);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: 140%;
`;

/**
 * The shared Textarea, which brings the things the local one was missing: a real
 * focus state (it had bare `outline: none` and nothing else — the worst violation
 * in this file), --font-primary in place of a hardcoded 'Inter', --radius-input,
 * and --foreground for typed text instead of the muted --text-secondary.
 *
 * Two consumer-level overrides on the inner field, both to keep it sized for a
 * 350px rail rather than a dialog: --text-xs to match every other label in this
 * panel, and the existing 80px height (the primitive's min-height: 100px plus
 * resize: vertical would grow the field and let it be dragged taller, neither of
 * which this row needs).
 */
const TextareaGuard = styled.div`
  width: 100%;
`;

const CustomPresetTextArea = styled(Textarea)`
  & textarea {
    min-height: 0;
    height: 80px;
    resize: none;
    padding: var(--spacing-sm);
    font-size: var(--text-xs);
    line-height: 150%;
  }
`;
